import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { gql } from '@apollo/client';
import { appServiceLines, commonEnums } from '@codametrix/ui-common';
import { getServiceDeskClient } from '../clients/service-desk.client';

// The BFF stores the BSP / "Bedside Pro" service line under the canonical key 'IPC'
// (see service-desk-directory-service migration WORKQ-687-2). The picker emits user-facing
// names. Collapse to the backend's canonical token before issuing the GraphQL filter.
const toBackendServiceLine = (service: string): string =>
  appServiceLines.isIPC(service) ? commonEnums.ServiceLines.IPC : service;

export interface QueueItem {
  id: string;
  queueName: string;
  status: 'healthy' | 'at-risk' | 'critical' | 'needs-attention' | 'superstar';
  coderCount: number;
  totalInQueue: number;
  criticalAged: number;
}

// Queue Metrics API (CDMX-14110) exposes per-collection numbers as a
// { metric_key, value } array on each priority profile. Map the backend's
// snake_case metric keys onto the QueueItem fields the card renders.
const METRIC_KEY = {
  codersAssigned: 'coders_assigned',
  totalInQueue: 'total_in_queue',
  criticalAged: 'critically_aged'
} as const;

const metricValue = (
  metrics: Array<{ metric_key: string; value: string }> | undefined,
  key: string
): number => parseInt(metrics?.find(m => m.metric_key === key)?.value ?? '0', 10);

const GET_PRIORITY_PROFILES = gql`
  query GetPriorityProfiles($pageNumber: Int, $pageSize: Int, $service: String) {
    GetPriorityProfiles(pageNumber: $pageNumber, pageSize: $pageSize, service: $service) {
      data {
        collection_id
        name
        availability
        metrics {
          metric_key
          value
        }
      }
      total
    }
  }
`;

const UPDATE_COLLECTION_CONFIG = gql`
  mutation UpdateCollectionConfig(
    $config: JSON
    $service: String
    $priorityOrder: [String]
  ) {
    UpdateCollectionConfig(
      config: $config
      service: $service
      priorityOrder: $priorityOrder
    )
  }
`;

const fetchQueues = createAsyncThunk<QueueItem[], { service: string }>(
  'queuePriorityOrder/fetchQueues',
  async ({ service }) => {
    const client = getServiceDeskClient();
    await client.refreshToken();
    const { data } = await client.apolloClient.query({
      query: GET_PRIORITY_PROFILES,
      variables: {
        service: toBackendServiceLine(service),
        pageNumber: 1,
        pageSize: 500
      },
      fetchPolicy: 'network-only'
    });
    const items: any[] = data?.GetPriorityProfiles?.data ?? [];
    return items.map(p => ({
      id: p.collection_id,
      queueName: p.name,
      coderCount: metricValue(p.metrics, METRIC_KEY.codersAssigned),
      totalInQueue: metricValue(p.metrics, METRIC_KEY.totalInQueue),
      criticalAged: metricValue(p.metrics, METRIC_KEY.criticalAged),
      // TODO(CDMX-12009): replace with real status when backend status calc lands
      status: 'healthy' as const
    }));
  }
);

const savePriorityOrder = createAsyncThunk<
  string[],
  { service: string; orderedIds: string[] }
>('queuePriorityOrder/savePriorityOrder', async ({ service, orderedIds }) => {
  const client = getServiceDeskClient();
  await client.refreshToken();
  await client.apolloClient.mutate({
    mutation: UPDATE_COLLECTION_CONFIG,
    variables: {
      service: toBackendServiceLine(service),
      priorityOrder: orderedIds,
      config: {}
    }
  });
  return orderedIds;
});

const reorderQueues = createAction<{ fromIndex: number; toIndex: number }>(
  'queuePriorityOrder/reorderQueues'
);

const revertOrder = createAction('queuePriorityOrder/revertOrder');

// Optimistically set a queue card's coder count after a Manage Coders save. The card's coderCount
// comes from the cron-cached coders_assigned metric (refreshed asynchronously via triggerRefresh),
// so re-reading it immediately would show a stale value. Patch it client-side to the modal's new
// total so the card reflects the change at once; fetchQueues reconciles on the next page load.
const setQueueCoderCount = createAction<{ queueId: string; coderCount: number }>(
  'queuePriorityOrder/setQueueCoderCount'
);

export {
  fetchQueues,
  savePriorityOrder,
  reorderQueues,
  revertOrder,
  setQueueCoderCount
};
